import { Controller } from "@hotwired/stimulus"
import { KTDataTable } from "@keenthemes/ktui"

export default class extends Controller {
  static values = { url: String }
  static targets = [
    "table",
    "search",
    "projectName",
    "province",
    "algorithmName",
  ]

  connect() {
    if (this.hasTableTarget) {
      this.datatable = new KTDataTable(this.tableTarget, {
        apiEndpoint: this.urlValue,
        infoEmpty: "暂无数据",
        loading: {
          template: `<div class="absolute inset-0 bg-white/0"></div>`,
          content: "",
        },
        columns: this.#getColumnDefinitions(),
        mapRequest: (params) => {
          const endpoint = this.datatable?._config?.apiEndpoint || this.urlValue
          if (endpoint) {
            const url = new URL(endpoint, window.location.origin)
            url.searchParams.forEach((v, k) => {
              if (!params.has(k)) params.set(k, v)
            })
          }

          if (this.hasSearchTarget && this.searchTarget.value) {
            params.set("search", this.searchTarget.value)
          }

          if (this.hasProvinceTarget) {
            if (this.provinceTarget.value) {
              params.set("province", this.provinceTarget.value)
            } else {
              params.delete("province")
            }
          }

          if (this.hasAlgorithmNameTarget) {
            if (this.algorithmNameTarget.value) {
              params.set("algorithm_name", this.algorithmNameTarget.value)
            } else {
              params.delete("algorithm_name")
            }
          }

          return params
        },
      })
    }
  }

  disconnect() {
    this.datatable?.dispose()
  }

  update() {
    this.datatable.goPage(1)
    this.datatable.reload()
  }

  changeTab(event) {
    event.preventDefault()

    const url = event.currentTarget.href
    if (!url) return

    this.datatable._config.apiEndpoint = url
    this.datatable.goPage(1)
    this.datatable.reload()

    this.element
      .querySelectorAll(".kt-tab-toggle")
      .forEach((tab) =>
        tab.classList.toggle("active", tab === event.currentTarget),
      )
  }

  #getColumnDefinitions() {
    const columns = {}
    const thElements = this.tableTarget.querySelectorAll(
      "thead th[data-kt-datatable-column]",
    )

    thElements.forEach((th) => {
      const columnName = th.getAttribute("data-kt-datatable-column")
      if (!columnName) return

      if (columnName === "provinces") {
        columns[columnName] = {
          render: (value) => {
            const values = Array.isArray(value) ? value : [value]
            if (values.length === 0) return "-"

            const valuesSpan = values
              .map(
                (v) =>
                  `<span class="kt-badge kt-badge-sm kt-badge-primary kt-badge-outline">${v}</span>`,
              )
              .join(" ")

            return `<div class="flex flex-wrap items-center gap-0.5">${valuesSpan}</div>`
          },
        }
      } else if (columnName === "actions") {
        columns[columnName] = {
          render: (item, row) => {
            // 如果后端返回的是 HTML（以 < 开头），直接使用
            if (typeof item === "string" && item.trim().startsWith("<")) {
              return item
            }

            // 默认处理：编辑用户链接
            return `
              <a class="kt-menu-link kt-btn kt-btn-sm kt-btn-icon kt-btn-outline inline-flex"
                 href="/admin/users/${row.id}/edit" data-turbo-frame="admin-content">
                <i class="ki-filled ki-pencil"></i>
              </a>
              `
          },
        }
      } else if (
        columnName === "predicted_values" ||
        columnName === "real_time" ||
        columnName === "day_ahead"
      ) {
        columns[columnName] = {
          render: (value, row) => {
            const values = Array.isArray(value) ? value : [value]
            if (values.length === 0) return "-"
            const rowId = row.id

            const safeColumn = String(columnName).replace(
              /[^a-zA-Z0-9_-]/g,
              "_",
            )
            const modalId = `row_${rowId}_${safeColumn}`

            const formatValue = (v) => {
              if (v === null || v === undefined || v === "") return "-"
              const num = parseFloat(v)
              return isNaN(num) ? "-" : num.toFixed(2)
            }

            return `
            <div>
            <span>
              ${values.slice(0, 3).map(formatValue).join(", ")},
              <a class="kt-link" data-kt-modal-toggle="#row_${modalId}">...</a>
            </span>
            <div class="kt-modal kt-modal-center" data-kt-modal="true" id="row_${modalId}">
              <div class="kt-modal-content max-w-[400px] max-h-[95%]">
                <div class="kt-modal-header">
                  <h3 class="kt-modal-title">所有数值</h3>
                  <button
                    type="button"
                    class="kt-modal-close"
                    aria-label="Close modal"
                    data-kt-modal-dismiss="#row_${modalId}"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-x"
                      aria-hidden="true"
                    >
                      <path d="M18 6 6 18"></path>
                      <path d="m6 6 12 12"></path>
                    </svg>
                  </button>
                </div>
                <div class="kt-modal-body">
                  <div class="rounded-lg bg-muted w-full grow">${values
                    .map(formatValue)
                    .join(", ")}</div>
                </div>
              </div>
            </div>
          </div>
            `
          },
        }
      } else {
        columns[columnName] = {
          render: (value) => (value === null ? "" : String(value)),
        }
      }
    })

    return columns
  }
}
